import { useContext, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Caption1,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Subtitle1,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { AddRegular, CheckmarkRegular, MoreHorizontalRegular, WarningRegular } from '@fluentui/react-icons';

import { ToastContext } from '@shared/contexts';

import { AddTaskDialog, TaskDetailsDialog, TaskGroupList } from '../components/plan';
import {
  AttentionSummary,
  CompleteTaskDialog,
  ContextualRollups,
  EXECUTION_FILTER,
  ExecutionFilters,
  ReportImpactDialog,
  TExecutionFilter,
} from '../components/execution';
import { getChangeV2CompletionPath, getChangeV2PlanPath } from '../helpers';
import { CHANGE_STATUS, TASK_TYPE, TPlanTask } from '../models';
import { TAddTaskInput, TCompleteTaskInput, TReportImpactInput, useChangeV2Store } from '../state/ChangeV2Store';
import {
  activeTasks,
  formatDate,
  getReleaseReadiness,
  isAwaitingExternal,
  isTaskComplete,
  isTaskDueSoon,
  isTaskOverdue,
  taskNeedsCompletionDialog,
} from '../utils';

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalL,
    flexWrap: 'wrap',
  },
  headLeft: { display: 'flex', flexDirection: 'column', gap: '2px' },
  muted: { color: tokens.colorNeutralForeground3 },
  actions: { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
});

const matchesFilter = (task: TPlanTask, filter: TExecutionFilter, currentUser: string): boolean => {
  switch (filter) {
    case EXECUTION_FILTER.MINE:
      return task.owner === currentUser;
    case EXECUTION_FILTER.ATTENTION:
      return isTaskOverdue(task) || isTaskDueSoon(task) || isAwaitingExternal(task);
    case EXECUTION_FILTER.DOCUMENTS:
      return task.taskType === TASK_TYPE.DOCUMENT_REVISION;
    case EXECUTION_FILTER.REGULATORY:
      return task.taskType === TASK_TYPE.REGULATORY_ASSESSMENT || Boolean(task.external);
    case EXECUTION_FILTER.TRAINING:
      return task.taskType === TASK_TYPE.TRAINING_REQUIREMENT;
    case EXECUTION_FILTER.ALL:
    default:
      return true;
  }
};

// Screen 8 - Execution cockpit. Helps the change manager coordinate execution and
// see what needs attention without opening every task or document.
const ExecutionPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { changeId } = useParams();
  const { show } = useContext(ToastContext);
  const { getChange, currentUser, completeTask, reportNewImpact, addPlanTask, beginCompletion } = useChangeV2Store();

  const [openTaskId, setOpenTaskId] = useState<string | undefined>();
  const [completingTask, setCompletingTask] = useState<TPlanTask | undefined>();
  const [reportOpen, setReportOpen] = useState(false);
  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const [filter, setFilter] = useState<TExecutionFilter>(EXECUTION_FILTER.ALL);
  const [search, setSearch] = useState('');
  const [showCompleted, setShowCompleted] = useState(true);

  const change = changeId ? getChange(changeId) : undefined;

  const plan = change?.plan;
  const normalizedSearch = search.trim().toLocaleLowerCase();

  const filteredTasks = useMemo(() => {
    if (!plan) return [];
    return activeTasks(plan).filter((task) => {
      if (!showCompleted && isTaskComplete(task)) return false;
      if (!matchesFilter(task, filter, currentUser)) return false;
      if (!normalizedSearch) return true;
      return [task.title, task.owner ?? '', task.taskType].join(' ').toLocaleLowerCase().includes(normalizedSearch);
    });
  }, [plan, showCompleted, filter, currentUser, normalizedSearch]);

  if (!change) return null;

  if (!plan?.baseline) {
    return (
      <MessageBar intent="info">
        <MessageBarBody>
          Execution starts once the implementation plan is frozen.{' '}
          <Button appearance="transparent" size="small" onClick={() => navigate(getChangeV2PlanPath(change.id))}>
            Open the plan
          </Button>
        </MessageBarBody>
      </MessageBar>
    );
  }

  const active = activeTasks(plan);
  const completed = active.filter(isTaskComplete).length;
  const openTask = plan.tasks.find((task) => task.id === openTaskId);
  const completionReadiness = getReleaseReadiness(change);
  const completionStarted = Boolean(change.completion) || change.status === CHANGE_STATUS.CLOSED;

  const handleCompleteRequest = (task: TPlanTask) => {
    if (taskNeedsCompletionDialog(task)) {
      setCompletingTask(task);
    } else {
      completeTask(change.id, task.id, {});
      show('success', 'Task completed.');
    }
  };

  const handleCompleteConfirm = (input: TCompleteTaskInput) => {
    if (!completingTask) return;
    completeTask(change.id, completingTask.id, input);
    setCompletingTask(undefined);
    show('success', 'Task completed.');
  };

  const handleReportImpact = (input: TReportImpactInput) => {
    reportNewImpact(change.id, input);
    setReportOpen(false);
    show(
      input.materialScopeChange === 'Yes' ? 'warning' : 'success',
      input.materialScopeChange === 'Yes'
        ? 'Material impact reported. The change now requires reassessment.'
        : 'Impact reported.'
    );
  };

  const handleAddTask = (input: TAddTaskInput) => {
    addPlanTask(change.id, input);
    show('success', 'Task added.');
  };

  const handleBeginCompletion = () => {
    if (!completionStarted) {
      beginCompletion(change.id);
      show('success', 'Completion started.');
    }
    navigate(getChangeV2CompletionPath(change.id));
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.headLeft}>
          <Subtitle1>Execution</Subtitle1>
          <Caption1 className={classes.muted}>
            Stage 6 of 9 · {completed} of {active.length} tasks complete
            {plan.targetDate ? ` · Target ${formatDate(plan.targetDate)}` : ''}
          </Caption1>
        </div>
        <div className={classes.actions}>
          <Button
            appearance="primary"
            icon={<CheckmarkRegular />}
            disabled={!completionStarted && !completionReadiness.eligible}
            onClick={handleBeginCompletion}
          >
            {completionStarted ? 'Open completion' : 'Start completion'}
          </Button>
          <Button appearance="secondary" icon={<WarningRegular />} onClick={() => setReportOpen(true)}>
            Report new impact
          </Button>
          <Button appearance="secondary" icon={<AddRegular />} onClick={() => setAddTaskOpen(true)}>
            Add task
          </Button>
          <Menu positioning="below-end">
            <MenuTrigger disableButtonEnhancement>
              <Button appearance="subtle" icon={<MoreHorizontalRegular />} aria-label="More actions" />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem onClick={() => navigate(getChangeV2PlanPath(change.id))}>View approved plan</MenuItem>
                <MenuItem
                  onClick={() => show('info', 'The activity and comments view is out of scope in this prototype.')}
                >
                  Activity and comments
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      </div>

      {plan.reassessmentRequired ? (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Reassessment required</MessageBarTitle>A material impact was reported. Release-readiness
            cannot be requested until it is resolved.
          </MessageBarBody>
        </MessageBar>
      ) : null}

      <AttentionSummary plan={plan} />

      <ExecutionFilters
        filter={filter}
        onFilterChange={setFilter}
        search={search}
        onSearchChange={setSearch}
        showCompleted={showCompleted}
        onShowCompletedChange={setShowCompleted}
      />

      {filteredTasks.length > 0 ? (
        <TaskGroupList
          change={change}
          tasks={filteredTasks}
          mode="execution"
          onOpenTask={setOpenTaskId}
          onCompleteTask={handleCompleteRequest}
        />
      ) : (
        <MessageBar intent="info">
          <MessageBarBody>No tasks match the current filters.</MessageBarBody>
        </MessageBar>
      )}

      <ContextualRollups plan={plan} />

      <TaskDetailsDialog
        change={change}
        task={openTask}
        mode="execution"
        onCompleteTask={handleCompleteRequest}
        onClose={() => setOpenTaskId(undefined)}
      />

      <CompleteTaskDialog
        task={completingTask}
        onClose={() => setCompletingTask(undefined)}
        onConfirm={handleCompleteConfirm}
      />

      <ReportImpactDialog open={reportOpen} onClose={() => setReportOpen(false)} onConfirm={handleReportImpact} />

      <AddTaskDialog plan={plan} open={addTaskOpen} onClose={() => setAddTaskOpen(false)} onSubmit={handleAddTask} />
    </div>
  );
};

export default ExecutionPage;
